import FeatureLockedCard from "@/components/feature/FeatureLockedCard";
import { useEntitlements } from "@/contexts/EntitlementsContext";
import { KpiGrid, ShareList, VolumeBars } from "./ReportingChartBlocks";
import BotQaSection from "./BotQaSection";

/**
 * @param {{ bot: object | null, noWorkspace: boolean, onQaComplete?: () => void }} props
 */
export default function BotAnalyticsPanel({ bot, noWorkspace, onQaComplete }) {
  const { hasFeature } = useEntitlements();
  if (!bot) return null;

  const kpis = Array.isArray(bot.kpis) ? bot.kpis.slice(0, 4) : [];
  const volumeWeek = Array.isArray(bot.volumeWeek) ? bot.volumeWeek : [];
  const volumeMax = typeof bot.volumeMax === "number" ? bot.volumeMax : 1;
  const meta = bot.meta && typeof bot.meta === "object" ? bot.meta : {};

  const volumeDays = volumeWeek.map((d) => ({
    day: d.day,
    date: d.date,
    sessions: d.sessions ?? 0,
  }));

  return (
    <>
      {noWorkspace ? (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
          Select a workspace to load chatbot metrics (same scope as Home and Live chat).
        </div>
      ) : null}

      <div className="mb-6 flex flex-col justify-between gap-2 border-b border-slate-100 pb-6 sm:flex-row sm:items-end dark:border-slate-800">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Chatbot overview</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            KPIs, volume, handoffs, satisfaction, and topics for the selected UTC date range.
          </p>
        </div>
        {typeof meta.sessionTotal === "number" && meta.periodStart && meta.periodEnd ? (
          <p className="text-xs font-medium text-slate-400 dark:text-slate-500">
            {meta.sessionTotal} bot sessions · {meta.periodStart} → {meta.periodEnd} UTC
          </p>
        ) : null}
      </div>

      <KpiGrid items={kpis} />

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <VolumeBars
          title="Bot conversation volume"
          subtitle="Daily bot sessions in the reporting window."
          days={volumeDays}
          max={volumeMax}
          keys={[{ key: "sessions", className: "bg-violet-600 dark:bg-violet-500" }]}
          legend={[{ label: "Sessions", dot: "bg-violet-600 dark:bg-violet-500" }]}
        />
        <ShareList
          title="Handoffs by reason"
          subtitle="Escalations from bot to human (excluding user drop-off)."
          rows={bot.handoffShare ?? []}
          emptyText="No handoff events in this range."
        />
        {hasFeature("sentiment_analysis") ? (
          <ShareList
            title="Sentiment mix"
            subtitle="Final sentiment classification per bot session."
            rows={bot.sentimentShare ?? []}
            emptyText="No sentiment data in this range."
          />
        ) : (
          <FeatureLockedCard
            feature="sentiment_analysis"
            title="Sentiment mix"
            subtitle="See how customers felt across bot conversations."
          />
        )}
      </div>

      {bot.volumeEmpty ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          No volume rows in this range — try widening the date range or confirm bot sessions exist for this workspace.
        </p>
      ) : null}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ShareList
          title="Thumbs feedback"
          subtitle="User ratings on bot conversations."
          rows={bot.thumbsShare ?? []}
          emptyText="No thumbs feedback in this range."
        />
        <div className="ui-card">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Star rating distribution</h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">1–5 star ratings in the selected period.</p>
          <div className="mt-6 space-y-3">
            {!bot.ratingRows?.length ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No star ratings in this range.</p>
            ) : (
              bot.ratingRows.map((r) => (
                <div key={r.stars} className="flex items-center gap-3">
                  <span className="w-8 text-xs font-semibold text-slate-500 dark:text-slate-400">{r.stars}★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200/60 dark:bg-slate-800 dark:ring-slate-700/60">
                    <div className="h-full rounded-full bg-violet-600 dark:bg-violet-500" style={{ width: `${r.pctWidth}%` }} />
                  </div>
                  <span className="w-8 text-right text-xs font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                    {r.count}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="ui-card">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Word cloud</h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Frequent words from user messages in this workspace.
          </p>
          {!(bot.wordCloud?.length || bot.trendingTopics?.length) ? (
            <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">No word data in this range.</p>
          ) : (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
              {(bot.wordCloud?.length ? bot.wordCloud : bot.trendingTopics).map((t) => (
                <span
                  key={`${t.word}-${t.count ?? t.rank}`}
                  className="font-semibold text-violet-700 dark:text-violet-300"
                  style={{ fontSize: `${t.fontPx || 14}px` }}
                >
                  {t.word}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="ui-card">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Most reported issues</h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Negative feedback comments grouped by phrase.
          </p>
          {!bot.reportedIssues?.length ? (
            <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">No negative comments in this range.</p>
          ) : (
            <ul className="mt-6 divide-y divide-slate-100 dark:divide-slate-800">
              {bot.reportedIssues.slice(0, 12).map((issue) => (
                <li key={issue.phrase} className="flex items-start gap-3 py-2.5 first:pt-0">
                  <span className="flex-1 text-sm text-slate-800 dark:text-slate-200">{issue.phrase}</span>
                  <span className="text-xs font-semibold tabular-nums text-rose-700 dark:text-rose-300">
                    {issue.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {hasFeature("auto_qa") ? (
        <BotQaSection botQa={bot.botQa} noWorkspace={noWorkspace} onQaComplete={onQaComplete} />
      ) : (
        <FeatureLockedCard
          feature="auto_qa"
          title="Quality & coaching"
          subtitle="AI-graded bot conversations with coaching notes for your team."
        />
      )}
    </>
  );
}
